import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { Download, Loader2 } from 'lucide-react';

import { formatSkillCommand, stripLeadingSlash } from '../../../lib/skill-command-utils';

function getSkillOfferHandle(filename: string) {
  return filename.replace(/\.[^.]+$/, '').trim() || 'custom-skill';
}

function getSkillOfferDisplayHandle({ commandHandle, filename }: { commandHandle?: string | null; filename: string }) {
  return stripLeadingSlash(commandHandle).trim() || getSkillOfferHandle(filename);
}

export function SkillOfferCard({
  commandHandle,
  filename,
  onDownload,
  onSave,
  isDownloading = false,
  isSaving = false,
  isSaved = false,
  showSave = true,
}: {
  commandHandle?: string | null;
  filename: string;
  onDownload?: () => void;
  onSave?: () => void;
  isDownloading?: boolean;
  isSaving?: boolean;
  isSaved?: boolean;
  showSave?: boolean;
}) {
  const handle = getSkillOfferDisplayHandle({ commandHandle, filename });

  return (
    <div className="flex flex-col gap-3">
      <p className="px-[10px] text-[14px] leading-[26px] text-[#262D46]">
        Here&apos;s your skill. Save it to add it to your / command picker.
      </p>
      <div className="px-[10px]">
        <div className="bg-muted flex items-center justify-between gap-4 overflow-hidden rounded-[20px] border border-[#D7E0EA] px-5 py-4">
          <div className="min-w-0 space-y-1">
            <div className="truncate text-[14px] leading-5 font-semibold text-[#262D46]">
              {formatSkillCommand(handle)}
            </div>
            <div className="truncate text-[12px] leading-4 text-[#546A83]">
              Generated Nova skill bundle ready to download or save as a custom skill.
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              data-testid="skill-offer-download-button"
              onClick={onDownload}
              disabled={!onDownload || isDownloading}
              className={cn('text-[#9CA9BA] transition-colors hover:text-[#546A83]', {
                'cursor-not-allowed opacity-50': !onDownload || isDownloading,
              })}
              aria-label={`Download ${filename}`}
            >
              {isDownloading ? <Loader2 className="size-6 animate-spin" /> : <Download className="size-6" />}
            </button>
            {showSave ? (
              <Button
                type="button"
                data-testid="skill-offer-save-button"
                onClick={onSave}
                disabled={!onSave || isSaving || isSaved}
                className={cn(
                  'rounded-full border-0 px-4 py-2 text-[14px] font-semibold text-[#3C461E]',
                  'bg-[linear-gradient(113deg,#A4F5FF_0%,#E7FC53_122.24%)] shadow-none hover:opacity-95',
                  isSaved && 'bg-[#E7EDF3] text-[#546A83] hover:opacity-100',
                )}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Saving...
                  </>
                ) : isSaved ? (
                  'Saved'
                ) : (
                  'Save skill'
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
